import React, { useMemo } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { useTranslation, Trans } from 'react-i18next';
import { markGuidedTourCompleted, persistGuidedTourToServer } from '../utils/guidedTourStorage';

export { hasCompletedGuidedTour, markGuidedTourCompleted } from '../utils/guidedTourStorage';

const tourText = {
  title: { fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '0.65rem', lineHeight: 1.3 },
  body: { fontSize: '1.05rem', lineHeight: 1.6, color: '#374151', marginBottom: 0 },
};

/**
 * Guided tour for core sidebar features. Runs from the dashboard so the left nav is visible.
 */
export default function AppGuidedTour({ run, onClose, onExit, storeId }) {
  const { t, i18n } = useTranslation();

  const steps = useMemo(
    () => [
      {
        target: 'body',
        content: (
          <div style={{ maxWidth: 480 }}>
            <p className="mb-3 fw-bold" style={{ ...tourText.title, fontSize: '1.35rem' }}>
              {t('guidedTour.introTitle')}
            </p>
            <p style={tourText.body}>{t('guidedTour.introBody')}</p>
          </div>
        ),
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '#tour-nav-seo',
        content: (
          <div style={{ maxWidth: 480 }}>
            <p style={tourText.title}>{t('guidedTour.aiSeoTitle')}</p>
            <p style={tourText.body}>
              <strong>{t('guidedTour.aiSeoBodyLead')}</strong> {t('guidedTour.aiSeoBodyRest')}
            </p>
          </div>
        ),
        disableBeacon: true,
      },
      {
        target: '#tour-nav-seo-bulk',
        content: (
          <div style={{ maxWidth: 500 }}>
            <p style={tourText.title}>{t('guidedTour.bulkTitle')}</p>
            <div
              role="img"
              aria-label={t('guidedTour.bulkTemplatesAria')}
              style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
                border: '2px solid #93c5fd',
                borderRadius: 12,
                padding: '16px 18px',
                marginBottom: 16,
              }}
            >
              <p
                style={{
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: '#1d4ed8',
                  marginBottom: 12,
                }}
              >
                {t('guidedTour.bulkTemplatesHeading')}
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#1e293b',
                }}
              >
                <span
                  style={{
                    background: '#fff',
                    border: '2px dashed #3b82f6',
                    borderRadius: 10,
                    padding: '12px 16px',
                    boxShadow: '0 2px 8px rgba(37,99,235,0.12)',
                  }}
                >
                  {t('guidedTour.bulkOneTemplate')}
                </span>
                <span style={{ color: '#64748b', fontSize: '1.4rem' }} aria-hidden>
                  →
                </span>
                <span
                  style={{
                    background: '#fff',
                    border: '2px solid #22c55e',
                    borderRadius: 10,
                    padding: '12px 16px',
                    boxShadow: '0 2px 8px rgba(34,197,94,0.12)',
                  }}
                >
                  {t('guidedTour.bulkManyPages')}
                </span>
              </div>
              <p style={{ ...tourText.body, marginTop: 14, fontSize: '1.02rem' }}>
                {t('guidedTour.bulkBodyBefore')}
                <strong>{t('guidedTour.bulkBodyStrong')}</strong>
                {t('guidedTour.bulkBodyAfter')}
              </p>
            </div>
          </div>
        ),
        disableBeacon: true,
      },
      {
        target: '#tour-nav-alt-text',
        content: (
          <div style={{ maxWidth: 480 }}>
            <p style={tourText.title}>{t('guidedTour.altTitle')}</p>
            <p style={tourText.body}>{t('guidedTour.altBody')}</p>
          </div>
        ),
        disableBeacon: true,
      },
      {
        target: '#tour-nav-image-opt',
        content: (
          <div style={{ maxWidth: 480 }}>
            <p style={tourText.title}>{t('guidedTour.imageTitle')}</p>
            <p style={tourText.body}>{t('guidedTour.imageBody')}</p>
          </div>
        ),
        disableBeacon: true,
      },
      {
        target: '#tour-nav-bulk-product-editor',
        content: (
          <div style={{ maxWidth: 480 }}>
            <p style={tourText.title}>{t('guidedTour.bulkEditorTitle')}</p>
            <p style={tourText.body}>{t('guidedTour.bulkEditorBody')}</p>
          </div>
        ),
        disableBeacon: true,
      },
      {
        target: '#tour-nav-page-speed',
        content: (
          <div style={{ maxWidth: 480 }}>
            <p style={tourText.title}>{t('guidedTour.pageSpeedTitle')}</p>
            <p style={tourText.body}>{t('guidedTour.pageSpeedBody')}</p>
          </div>
        ),
        disableBeacon: true,
      },
      {
        target: 'body',
        content: (
          <div style={{ maxWidth: 480 }}>
            <p className="mb-3 fw-bold" style={{ ...tourText.title, fontSize: '1.35rem' }}>
              {t('guidedTour.outroTitle')}
            </p>
            <p style={tourText.body}>
              <Trans i18nKey="guidedTour.outroBody" components={{ learn: <strong />, appMap: <strong /> }} />
            </p>
          </div>
        ),
        placement: 'center',
        disableBeacon: true,
      },
    ],
    [t, i18n.language]
  );

  const handleCallback = (data) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      markGuidedTourCompleted();
      if (storeId) {
        void persistGuidedTourToServer(storeId);
      }
      const reason = status === STATUS.FINISHED ? 'finished' : 'skipped';
      onClose();
      window.requestAnimationFrame(() => {
        if (onExit) onExit(reason);
      });
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      scrollToFirstStep
      scrollOffset={120}
      disableScrolling={false}
      spotlightPadding={8}
      callback={handleCallback}
      styles={{
        options: {
          zIndex: 11000,
          primaryColor: '#0d6efd',
          textColor: '#1f2937',
          width: undefined,
        },
        tooltip: {
          borderRadius: 16,
          fontSize: 17,
          padding: '22px 26px',
          minWidth: 420,
          maxWidth: 'min(560px, calc(100vw - 32px))',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.18)',
        },
        tooltipContainer: {
          maxWidth: 'min(560px, calc(100vw - 32px))',
        },
        tooltipContent: {
          padding: '4px 2px 8px',
          fontSize: 17,
          lineHeight: 1.55,
        },
        tooltipFooter: {
          marginTop: 18,
        },
        buttonNext: {
          borderRadius: 10,
          fontWeight: 600,
          fontSize: 16,
          padding: '11px 20px',
        },
        buttonBack: {
          color: '#6b7280',
          fontSize: 16,
        },
        buttonSkip: {
          color: '#6b7280',
          fontSize: 15,
        },
        buttonClose: {
          height: 14,
          width: 14,
          padding: 8,
        },
      }}
      locale={{
        back: t('guidedTour.btnBack'),
        close: t('guidedTour.btnClose'),
        last: t('guidedTour.btnDone'),
        next: t('guidedTour.btnNext'),
        skip: t('guidedTour.btnSkip'),
      }}
    />
  );
}
